import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { enqueueSnackbar } from 'notistack';
import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';

import CustomModal from '@/components/CustomModal';
import DatePicker from '@/components/DatePicker';
import Input from '@/components/Input/Input';
import Select from '@/components/Select';
import { Switch } from '@/components/Switch';
import { CURRENCY_OPTIONS } from '@/constants/constant';
import { ECurrency, ERoleAccess } from '@/enum';
import useAccess from '@/hooks/useAccess';
import { useFormatText } from '@/hooks/useFormatText';
import {
  addSchoolSchema,
  TAddSchoolFormValues,
} from '@/schema/addSchoolSchema';
import { SUBSCRIPTION_INPUT_MAX_LENGTH } from '@/schema/subscriptionSchema';
import { addSchoolData, updateSchoolImage } from '@/service/schoolService';
import { IAddSchoolPayload } from '@/types/payload';
import { getTodayIso, getNextDayIso, getPrevDayIso } from '@/utils';
import { zodResolver } from '@hookform/resolvers/zod';

import PhoneNumberInput from '../components/PhoneInput';
import { ProfileImagePicker } from '../components/ProfileImagePicker';

type TAddSchoolFormValuesWithImage = TAddSchoolFormValues & {
  profileImage?: File;
};

dayjs.extend(utc);

const AddSchoolModal = ({
  setIsOpen,
  onSubmit,
}: {
  setIsOpen: (isOpen: boolean) => void;
  onSubmit: () => void;
}) => {
  const { hasAccess } = useAccess();
  const [isLoading, setIsLoading] = useState(false);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [assetId, setAssetId] = useState<string | null>(null);
  const { formatText } = useFormatText();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = useForm<TAddSchoolFormValuesWithImage>({
    mode: 'onChange',
    resolver: zodResolver(addSchoolSchema),
    defaultValues: {
      countryCode: '+965',
      status: true,
      sendInvite: true,
      mobileNumber: '',
      currency: ECurrency.KWD,
      subscriptionStartDate: '',
      subscriptionEndDate: '',
      transactionId: '',
    },
  });

  const todayIso = getTodayIso();
  const subscriptionStartDate = watch('subscriptionStartDate');
  const subscriptionEndDate = watch('subscriptionEndDate');
  const minEndDate = subscriptionStartDate
    ? getNextDayIso(subscriptionStartDate)
    : todayIso;
  const maxStartDate = subscriptionEndDate
    ? getPrevDayIso(subscriptionEndDate)
    : undefined;

  const handleProfileImageChange = async (file?: File) => {
    setValue('profileImage', file);

    if (!file) {
      setAssetId(null);
      return;
    }
    setIsProfileLoading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('folder', 'school-image');

      const response = await updateSchoolImage(formData);

      if (response?.status && response?.data?.id) {
        setAssetId(response.data.id);
      } else {
        console.error('Image upload failed:', response);
      }
    } catch (error) {
      console.error('Image upload error:', error);
    } finally {
      setIsProfileLoading(false);
    }
  };

  const handleOnSubmit = async (data: TAddSchoolFormValuesWithImage) => {
    setIsLoading(true);
    try {
      const formData: IAddSchoolPayload = {
        name: data.schoolName,
        email: data.schoolEmail,
        phone_code: data.countryCode,
        phone_number: data.mobileNumber,
        send_email: data.sendInvite,
        is_active: data.status,
        asset_id: assetId ?? undefined,
        subscription: {
          student_limit: data.studentLimit,
          amount: data.amount,
          currency: data.currency,
          start_date: data.subscriptionStartDate,
          end_date: data.subscriptionEndDate,
          transaction_id: data.transactionId,
        },
      };
      const response = await addSchoolData(formData);

      if (response?.status) {
        enqueueSnackbar(response?.message, { variant: 'success' });
        setIsOpen(false);
        onSubmit();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!hasAccess(ERoleAccess.USER_MANAGEMENT)) return null;

  const isSubmitLoading = isLoading || isSubmitting;

  return (
    <CustomModal
      setIsOpen={setIsOpen}
      className="w-150 max-w-[90vw]"
      isFooter
      isHeader
      heading={formatText('school.add-school-form.heading')}
      position="right"
      cancelButtonProps={{
        label: formatText('common.actions.cancel'),
        desructive: true,
        disabled: isSubmitLoading,
        onClick: () => setIsOpen(false),
      }}
      confirmButtonProps={{
        label: formatText('school.actions.add-school'),
        variant: 'primary',
        onClick: handleSubmit(handleOnSubmit),
        loading: isSubmitLoading,
        disabled: isSubmitLoading || isProfileLoading,
      }}
    >
      <div className="flex flex-col gap-4 w-full">
        {/* Profile Image */}
        <ProfileImagePicker
          value={watch('profileImage')}
          onChange={handleProfileImageChange}
          isLoading={isProfileLoading}
        />

        <Input
          label={formatText('school.add-school-form.school-name')}
          name="schoolName"
          placeholder={formatText(
            'school.add-school-form.school-name-placeholder'
          )}
          register={register}
          error={
            errors.schoolName?.message
              ? formatText(errors.schoolName.message)
              : undefined
          }
          isRequired
        />

        <Input
          label={formatText('school.add-school-form.school-email')}
          name="schoolEmail"
          placeholder={formatText(
            'school.add-school-form.school-email-placeholder'
          )}
          register={register}
          error={
            errors.schoolEmail?.message
              ? formatText(errors.schoolEmail.message)
              : undefined
          }
          isRequired
        />

        <PhoneNumberInput
          name="mobileNumber"
          label={formatText('school.add-school-form.school-phone')}
          placeholder={formatText(
            'school.add-school-form.school-phone-placeholder'
          )}
          register={register}
          setValue={setValue}
          countryFlag
          required
          countrySelected={watch('countryCode')}
          error={
            errors.mobileNumber?.message
              ? formatText(errors.mobileNumber.message)
              : undefined
          }
        />
        <Input
          label={formatText('school.add-school-form.student-limit')}
          name="studentLimit"
          type="number"
          placeholder={formatText(
            'school.add-school-form.student-limit-placeholder'
          )}
          min={1}
          maxlength={SUBSCRIPTION_INPUT_MAX_LENGTH}
          integer
          register={register}
          error={
            errors.studentLimit?.message
              ? formatText(errors.studentLimit.message)
              : undefined
          }
          isRequired
        />

        <div className="flex flex-col sm:flex-row gap-4 items-start">
          <Input
            label={formatText('school.add-school-form.amount')}
            name="amount"
            type="number"
            step="0.001"
            placeholder={formatText(
              'school.add-school-form.amount-placeholder'
            )}
            maxlength={SUBSCRIPTION_INPUT_MAX_LENGTH}
            register={register}
            error={
              errors.amount?.message
                ? formatText(errors.amount.message)
                : undefined
            }
            isRequired
            className="w-full sm:flex-1"
          />
          <Controller
            name="currency"
            control={control}
            render={({ field }) => (
              <Select
                name={formatText('school.add-school-form.currency')}
                placeholder={formatText(
                  'school.add-school-form.currency-placeholder'
                )}
                options={CURRENCY_OPTIONS}
                selected={field.value ?? ''}
                onChange={field.onChange}
                error={
                  errors.currency?.message
                    ? formatText(errors.currency.message)
                    : undefined
                }
                isRequired
                mainClassName="w-full sm:flex-1"
              />
            )}
          />
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <Controller
            name="subscriptionStartDate"
            control={control}
            render={({ field }) => (
              <div className="flex-1">
                <DatePicker
                  label={formatText(
                    'school.add-school-form.subscription-start-date'
                  )}
                  placeholder={formatText(
                    'school.add-school-form.subscription-start-date-placeholder'
                  )}
                  value={field.value || null}
                  onChange={field.onChange}
                  min={todayIso}
                  max={maxStartDate}
                  error={
                    errors.subscriptionStartDate?.message
                      ? formatText(errors.subscriptionStartDate.message)
                      : undefined
                  }
                  isRequired
                />
              </div>
            )}
          />

          <Controller
            name="subscriptionEndDate"
            control={control}
            render={({ field }) => (
              <div className="flex-1">
                <DatePicker
                  label={formatText(
                    'school.add-school-form.subscription-end-date'
                  )}
                  placeholder={formatText(
                    'school.add-school-form.subscription-end-date-placeholder'
                  )}
                  value={field.value || null}
                  onChange={field.onChange}
                  min={minEndDate}
                  error={
                    errors.subscriptionEndDate?.message
                      ? formatText(errors.subscriptionEndDate.message)
                      : undefined
                  }
                  isRequired
                />
              </div>
            )}
          />
        </div>

        <Input
          label={formatText('school.add-school-form.transaction-id')}
          name="transactionId"
          placeholder={formatText(
            'school.add-school-form.transaction-id-placeholder'
          )}
          register={register}
          error={
            errors.transactionId?.message
              ? formatText(errors.transactionId.message)
              : undefined
          }
          isRequired
        />

        <div className="flex items-center gap-3 pt-2">
          <Switch
            id="status"
            checked={watch('status')}
            onChange={(v) => setValue('status', v)}
          />
          <span>
            {formatText('school.columns.status')}:{' '}
            {formatText('common.status.active')}
          </span>
        </div>

        <div className="flex items-center gap-3">
          <Switch
            id="sendInvite"
            checked={watch('sendInvite')}
            onChange={(v) => setValue('sendInvite', v)}
          />
          <span>{formatText('school.add-school-form.send-invitaton')}</span>
        </div>
      </div>
    </CustomModal>
  );
};

export default AddSchoolModal;
